import type {
  BulkReprocessRunDtoMode,
  BulkReprocessRunDtoStatus,
  BulkReprocessRunItemDtoStatus,
} from '@sara/api-client';
import type { BadgeProps } from '@sara/ui/components/badge';
import { RefreshCw, RotateCcw, Webhook, type LucideIcon } from 'lucide-react';

/**
 * Single source of truth for how a run's mode is presented:
 *  - `label` — short, for the runs list / run detail chips.
 *  - `cardTitle` / `cardDescription` / `Icon` — for the launch form's mode cards.
 * Keyed by the string union shared across the generated `*Mode` enums.
 */
export const MODE_METADATA: Record<
  BulkReprocessRunDtoMode,
  { label: string; cardTitle: string; cardDescription: string; Icon: LucideIcon }
> = {
  FAILED: {
    label: 'Re-run failed',
    cardTitle: 'Re-run failed steps',
    cardDescription:
      'Re-run only the steps that failed the first time — anything that already succeeded, and the transcript, is kept. Use when part of the automated processing failed (e.g. scoring errored) but everything else is fine.',
    Icon: RotateCcw,
  },
  ALL: {
    label: 'Replay all',
    cardTitle: 'Replay everything',
    cardDescription:
      'Re-source the transcript and re-run extraction and rubric scoring from scratch, overwriting the existing results, then send the webhook. Use when the results themselves are wrong — or you changed the template or rubric. Uses AI credits.',
    Icon: RefreshCw,
  },
  RESEND: {
    label: 'Re-send webhook',
    cardTitle: 'Re-send webhook',
    cardDescription:
      'Re-deliver the interview.completed webhook to your configured endpoints using the results already on file. No re-processing. Use when the interview was scored correctly but the downstream system did not receive it — endpoint was down, misconfigured, or errored.',
    Icon: Webhook,
  },
};

/** Badge variant per run status — shared by the runs list and detail views. */
export const RUN_STATUS_VARIANT: Record<BulkReprocessRunDtoStatus, BadgeProps['variant']> = {
  PENDING: 'neutral',
  RUNNING: 'info',
  COMPLETED: 'success',
  FAILED: 'destructive',
  CANCELED: 'warning',
};

/** Badge variant per item status — the run detail's per-session drill-down. */
export const ITEM_STATUS_VARIANT: Record<BulkReprocessRunItemDtoStatus, BadgeProps['variant']> = {
  PENDING: 'neutral',
  RUNNING: 'info',
  SUCCEEDED: 'success',
  FAILED: 'destructive',
  SKIPPED: 'warning',
};
